import {
  getAuth,
  onAuthStateChanged,
  signOut,
  type User as AuthUser,
} from 'firebase/auth'
import type { User } from 'fitify-types/src/types/user'
import {
  cancellablePromise,
  isPromiseCancelledError,
} from 'fitify-utils/src/async/cancellable-promise'
import { useRouter } from 'next/router'
import {
  type Context,
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { fetchUserProfile } from '../api/nextApi'

type AuthUserContextData = {
  authUser: AuthUser | null
  userProfile: User | null
  isLoggedIn: boolean
  isAuthLoading: boolean
  logout: () => Promise<void>
  refreshUserProfile: () => Promise<void>
}

const AuthUserContext: Context<AuthUserContextData> =
  createContext<AuthUserContextData>({
    authUser: null,
    userProfile: null,
    isLoggedIn: false,
    isAuthLoading: true,
    logout: async () => Promise.resolve(undefined),
    refreshUserProfile: async () => Promise.resolve(undefined),
  })

export const AuthUserContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null)
  const previousUserRef = useRef<AuthUser | null>(null)
  const fetchUserProfilePromiseController = useRef<AbortController | undefined>(
    undefined
  )
  const [userProfile, setUserProfile] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAuthStateLoaded, setIsAuthStateLoaded] = useState<boolean>(false)

  const router = useRouter()

  const cleanContext = useCallback(() => {
    setAuthUser(null)
    setUserProfile(null)
    previousUserRef.current = null
    setIsLoading(false)
  }, [])

  const fetchProfile = useCallback(async () => {
    try {
      fetchUserProfilePromiseController.current?.abort()
      fetchUserProfilePromiseController.current = new AbortController()

      setIsLoading(true)
      const profile = await cancellablePromise(
        fetchUserProfile(),
        fetchUserProfilePromiseController.current.signal
      )
      setUserProfile(profile)
      setIsLoading(false)
    } catch (error) {
      if (!isPromiseCancelledError(error)) {
        console.error(error)
      }
    }
  }, [])

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    return onAuthStateChanged(getAuth(), (user) => {
      if (user) {
        setAuthUser(user)
        if (user.uid !== previousUserRef.current?.uid) {
          void fetchProfile()
          previousUserRef.current = user
        }
      } else {
        // Reset state if no user is authenticated
        cleanContext()
      }
      setIsAuthStateLoaded(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  const refreshUserProfile = useCallback(async () => {
    if (authUser) {
      await fetchProfile()
    }
  }, [authUser, fetchProfile])

  const value = useMemo(
    () => ({
      authUser,
      userProfile,
      isLoggedIn: !!authUser,
      isAuthLoading: isLoading || !isAuthStateLoaded,
      logout: async () => {
        await signOut(getAuth())
        cleanContext()
      },
      refreshUserProfile,
    }),
    [
      authUser,
      cleanContext,
      isAuthStateLoaded,
      isLoading,
      refreshUserProfile,
      userProfile,
    ]
  )

  return (
    <AuthUserContext.Provider value={value}>
      {children}
    </AuthUserContext.Provider>
  )
}

export const useAuthUserContext = () => {
  const context = useContext(AuthUserContext)
  if (!context) {
    throw new Error(
      'useAuthUserContext must be used within a AuthUserContextProvider'
    )
  }
  return context
}
